// src/components/TakeawayOrder.tsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    IconButton,
    Button,
    Snackbar,
    Alert,
    AlertColor,
    Grid2,
    Divider,
    CardMedia,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Meal, OrderItem, mealService } from '../services';
import DietaryLabel from './DietaryLabel';
import { format } from 'date-fns';

interface SnackbarState {
    open: boolean;
    message: string;
    severity: AlertColor;
}

const TakeawayOrder: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [meals, setMeals] = useState<Meal[]>([]);
    const [businessDays, setBusinessDays] = useState<{ date: string; dayName: string }[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [mealsData, nextBusinessDays] = await Promise.all([
                    mealService.getMeals(),
                    mealService.getNextBusinessDays(5)
                ]);
                setMeals(mealsData);
                setBusinessDays(nextBusinessDays);
                setSelectedDate(nextBusinessDays[0]?.date || '');
                setLoading(false);
            } catch (error) {
                console.error('Failed to fetch data:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to load data',
                    severity: 'error'
                });
            }
        };
        fetchData();
    }, []);

    const handleQuantityChange = (mealId: number, change: number) => {
        setOrderItems(prevItems => {
            const existingItem = prevItems.find(item => item.mealId === mealId);
            if (!existingItem && change > 0) {
                return [...prevItems, { mealId, quantity: 1 }];
            }

            return prevItems.map(item => {
                if (item.mealId === mealId) {
                    const newQuantity = Math.max(0, item.quantity + change);
                    return newQuantity === 0 ? null : { ...item, quantity: newQuantity };
                }
                return item;
            }).filter((item): item is OrderItem => item !== null);
        });
    };

    const calculateTotal = (): number => {
        return orderItems.reduce((total, item) => {
            const meal = meals.find(m => m.id === item.mealId);
            return total + (meal?.price ?? 0) * item.quantity;
        }, 0);
    };

    const handleSubmit = async () => {
        if (!selectedDate || orderItems.length === 0) {
            setSnackbar({
                open: true,
                message: 'Please select a date and at least one meal',
                severity: 'error'
            });
            return;
        }

        try {
            await mealService.createTakeawayOrder({
                date: selectedDate,
                items: orderItems,
                totalAmount: calculateTotal()
            });

            setSnackbar({
                open: true,
                message: t('takeawayOrderSuccess'),
                severity: 'success'
            });
            navigate('/takeaway-summary');
        } catch (error) {
            console.error('Failed to place takeaway order:', error);
            setSnackbar({
                open: true,
                message: 'Failed to place order',
                severity: 'error'
            });
        }
    };

    if (loading) {
        return <Typography>Loading...</Typography>;
    }

    return (
        <Box sx={{ maxWidth: 'lg', mx: 'auto', p: 3 }}>
            <Typography variant="h4" gutterBottom>
                {t('takeawayOrder')}
            </Typography>

            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    {t('selectDate')}
                </Typography>
                <Grid2 container spacing={2}>
                    {businessDays.map((day) => (
                        <Grid2 key={day.date}>
                            <Button
                                variant={selectedDate === day.date ? 'contained' : 'outlined'}
                                onClick={() => setSelectedDate(day.date)}
                            >
                                {t(day.dayName)}, {format(day.date, 'dd.MM.yyyy')}
                            </Button>
                        </Grid2>
                    ))}
                </Grid2>
            </Box>

            <Grid2 container spacing={3}>
                {meals.map((meal) => (
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={meal.id}>
                        <Card>
                            <CardMedia
                                component="img"
                                height="140"
                                image={meal.imagePath}
                                alt={meal.name}
                            />
                            <CardContent>
                                <Typography variant="h6">{meal.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {meal.description}
                                </Typography>
                                <DietaryLabel dietaryInfo={meal.dietaryInfo} />
                                <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
                                    ${meal.price.toFixed(2)}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                    <IconButton
                                        onClick={() => handleQuantityChange(meal.id, -1)}
                                        disabled={!orderItems.find(item => item.mealId === meal.id)}
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                    <Typography sx={{ mx: 2 }}>
                                        {orderItems.find(item => item.mealId === meal.id)?.quantity ?? 0}
                                    </Typography>
                                    <IconButton
                                        onClick={() => handleQuantityChange(meal.id, 1)}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid2>
                ))}
            </Grid2>

            <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="h5" gutterBottom>
                    {t('orderSummary')}
                </Typography>
                {orderItems.map((item) => {
                    const meal = meals.find(m => m.id === item.mealId);
                    return meal ? (
                        <Box key={item.mealId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography>
                                {meal.name} x {item.quantity}
                            </Typography>
                            <Typography>
                                ${(meal.price * item.quantity).toFixed(2)}
                            </Typography>
                        </Box>
                    ) : null;
                })}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h6">${calculateTotal().toFixed(2)}</Typography>
                </Box>
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={orderItems.length === 0 || !selectedDate}
                    size="large"
                >
                    {t('placeTakeawayOrder')}
                </Button>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TakeawayOrder;